import type { Ref } from 'react';
import { forwardRef, useCallback } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import PopupState, { bindToggle, bindPopper } from 'material-ui-popup-state';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { GrayOutlineButton } from '@trinity/chat-components/components/presentation/ButtonOutlined.js';
import type { WorkspaceObjectRole } from '@/enums';
import { WorkspaceRoleMap } from '@/enums';
import { cx } from '@trinity/chat-components';
import styles from './style/UserAccessContextMenu.module.css';

type MenuProps = Omit<Props, 'role'> & {
  onClose: () => void;
};

const UserActionsContextMenu = forwardRef(({ onChange, onRemove, roles, onClose }: MenuProps, ref: Ref<HTMLDivElement>) => {
  const closeWrapper = useCallback((fn: () => void) => {
    return () => {
      fn();
      onClose();
    };
  }, [onClose]);
  return (
    <PopperMenu className={styles.menu} ref={ref}>
      {roles.map(role => (
        <PopperMenuItem key={role} onClick={closeWrapper(() => onChange(role))}>
          {WorkspaceRoleMap[role]}
        </PopperMenuItem>
      ))}
      <div className={styles.divider} />
      <PopperMenuItem
        onClick={closeWrapper(onRemove)}>
        Remove
      </PopperMenuItem>
    </PopperMenu>
  );
});

type Props = {
  role: WorkspaceObjectRole;
  onChange: (role: WorkspaceObjectRole) => void;
  onRemove: () => void;
  roles: WorkspaceObjectRole[];
};

export const RoleSelector = ({ role, ...props }: Props) => {
  return (
    <PopupState variant="popper" popupId="user-object-access-popper">
      {popupState => (
        <div>
          <div {...bindToggle(popupState)}>
            <GrayOutlineButton
              onClick={popupState.open}
              className={cx(styles.roleBtn, { [styles.open]: popupState.isOpen })}>
              <div>{WorkspaceRoleMap[role]}</div>
              <div className={styles.chevron} />
            </GrayOutlineButton>
          </div>
          <Popper
            {...bindPopper(popupState)}
            placement="bottom-end"
            className={styles.popper}>
            <ClickAwayListener
              onClickAway={() => popupState.close()}>
              <UserActionsContextMenu
                onClose={popupState.close}
                {...props} />
            </ClickAwayListener>
          </Popper>
        </div>
      )}
    </PopupState>
  );
};

export default RoleSelector;